import { useMemo } from 'react';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { createColumnHelper } from '@tanstack/react-table';

import TrendSparkLineCell from '../components/diagnostics/TrendSparkLineCell';
import { AdGroupDiagnosticMetric } from '../types';

import { usePlatform } from '@/features/users';
import { Info } from '@/shared/components/icons';
import { Tooltip } from '@/shared/components/tooltip';

const columnHelper = createColumnHelper<AdGroupDiagnosticMetric>();

const useAdGroupDiagnosticsColumns = () => {
  const platform = usePlatform();
  const isMeta = platform === 'Meta';

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'nameAndId',
        header: () => (
          <Typography variant='body3' component='span'>
            {isMeta ? 'Ad Set Name and ID' : 'Ad Group Name and ID'}
          </Typography>
        ),

        cell: (props) => {
          const row = props.row.original;
          const displayName = isMeta
            ? (row.adsetName ?? row.adgroupName)
            : row.adgroupName;
          const displayId = isMeta
            ? (row.adsetId ?? row.adgroupId)
            : row.adgroupId;

          return (
            <Stack gap={0.75}>
              <Typography
                variant='subtitleLabel'
                component='span'
                whiteSpace='break-spaces'
                sx={{
                  wordBreak: 'break-word',
                }}
              >
                {displayName}
              </Typography>
              <Typography variant='subtitleLabel' component='span'>
                ({displayId})
              </Typography>
            </Stack>
          );
        },

        size: 200,
      }),

      columnHelper.accessor('totalSpend', {
        header: () => (
          <Typography variant='body3' component='span'>
            Total Spend
          </Typography>
        ),

        cell: (props) => (
          <Typography variant='subtitleLabel' component='span'>
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: 0,
            }).format(props.getValue())}
          </Typography>
        ),

        size: 110,
        enableSorting: false,
      }),

      columnHelper.accessor('yesterdaySpend', {
        header: () => (
          <Typography variant='body3' component='span'>
            Spend Yesterday
          </Typography>
        ),

        cell: (props) => (
          <Typography variant='subtitleLabel' component='span'>
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: 0,
            }).format(props.getValue())}
          </Typography>
        ),

        size: 120,
        enableSorting: false,
      }),

      columnHelper.accessor('kpiType', {
        header: () => (
          <Typography variant='body3' component='span'>
            KPI Type
          </Typography>
        ),

        cell: (props) => (
          <Typography variant='subtitleLabel' component='span'>
            {props.getValue()}
          </Typography>
        ),
        size: 100,
        enableSorting: false,
      }),

      columnHelper.accessor('kpiGoal', {
        header: () => (
          <Typography variant='body3' component='span'>
            KPI Goal
          </Typography>
        ),

        cell: (props) => (
          <Typography variant='subtitleLabel' component='span'>
            {props.getValue()}
          </Typography>
        ),
        size: 100,
        enableSorting: false,
      }),

      columnHelper.accessor('kpiActual.value', {
        header: () => (
          <Typography variant='body3' component='span'>
            KPI Actual
          </Typography>
        ),

        cell: (props) => (
          <Typography variant='subtitleLabel' component='span'>
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: 2,
            }).format(props.getValue())}
          </Typography>
        ),
        size: 100,
        enableSorting: false,
      }),

      columnHelper.accessor('kpiTrends', {
        header: () => (
          <Stack direction='row' alignItems='center' gap={0.5}>
            <Tooltip title='Over the past 7 days' placement='top'>
              <Info sx={{ color: 'text.secondary', fontSize: 16 }} />
            </Tooltip>
            <Typography variant='body3' component='span'>
              KPI Trend
            </Typography>
          </Stack>
        ),

        cell: (props) => <TrendSparkLineCell trends={props.getValue()} />,

        size: 150,
        enableSorting: false,
      }),

      columnHelper.accessor('kpiComparisonToAvg', {
        header: () => (
          <Typography variant='body3' component='span'>
            KPI Comparison to Average
          </Typography>
        ),

        cell: (props) => (
          <Typography variant='subtitleLabel' component='span'>
            {new Intl.NumberFormat('en-US', {
              style: 'percent',
              maximumFractionDigits: 1,
            }).format(props.getValue())}
          </Typography>
        ),

        size: 200,
        enableSorting: false,
      }),
    ],
    [isMeta]
  );

  return columns;
};

export default useAdGroupDiagnosticsColumns;
